/**
 * API-Klasse für die Kommunikation mit dem FireCore-Backend
 *
 * @module api
 */

import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

axios.defaults.baseURL = getApiBaseUrl();

// Leere oder ungültige JSON-Antworten abfangen (wenn API nicht antwortet)
axios.defaults.transformResponse = [(data, headers) => {
  if (data === undefined || data === null || (typeof data === 'string' && data.trim() === '')) {
    return null;
  }
  if (typeof data === 'object') {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch (_) {
    return null;
  }
}];

/**
 * Hauptklasse für API-Operationen
 */
class API {
  constructor() {
    this.endpoints = {};

    this.endpoints.user = this.createBasicCRUDEndpoints({ name: 'user' });
    this.endpoints.devices = this.createBasicCRUDEndpoints({ name: 'devices' });
    this.endpoints.devicesStatus = this.createBasicCRUDEndpoints({ name: 'devices-status' });
    this.endpoints.deviceLogs = this.createBasicCRUDEndpoints({ name: 'device-logs' });
    this.endpoints.devicesRequest = this.createBasicCRUDEndpoints({ name: 'devices-request' });
    this.endpoints.devicesRequestList = this.createBasicCRUDEndpoints({ name: 'devices-request-list' });
    this.endpoints.events = this.createBasicCRUDEndpoints({ name: 'events' });
    this.endpoints.me = this.createBasicCRUDEndpoints({ name: 'me' });
    this.endpoints.firmware = this.createBasicCRUDEndpoints({ name: 'firmware' });
    this.endpoints.actionStatus = this.createBasicCRUDEndpoints({ name: 'action-status' });
    this.endpoints.actionRestart = this.createBasicCRUDEndpoints({ name: 'action-restart' });
    this.endpoints.actionFirmwareDownload = this.createBasicCRUDEndpoints({ name: 'action-firmware-download' });
    this.endpoints.actionFirmwareWrite = this.createBasicCRUDEndpoints({ name: 'action-firmware-write' });
    this.endpoints.actionHistory = this.createBasicCRUDEndpoints({ name: 'action-history' });
    this.endpoints.actionHistoryDetails = this.createBasicCRUDEndpoints({ name: 'action-history-details' });
    this.endpoints.groups = this.createBasicCRUDEndpoints({ name: 'groups' });
    this.endpoints.password = this.createBasicCRUDEndpoints({ name: 'password' });
    this.endpoints.authenticationLogout = this.createBasicCRUDEndpoints({ name: 'authentication/logout' });
    this.endpoints.organisation = this.createBasicCRUDEndpoints({ name: 'organisation' });
    this.endpoints.live = {
      get: (config = { withCredentials: true, crossDomain: true }) =>
        axios.get('v2/live/', config).catch((err) => this.catchFuntion(err)),
      wait: (since = 0, config = { withCredentials: true, crossDomain: true }) =>
        axios.get(`v2/live/wait/?since=${encodeURIComponent(since)}`, { ...config, timeout: 18000 })
          .catch((err) => {
            if (err && (err.code === 'ECONNABORTED' || (err.message && err.message.includes('timeout')))) {
              return { data: null };
            }
            return this.catchFuntion(err);
          }),
    };
  }

  /**
   * @param {Object} options
   * @param {string} options.name
   */
  createBasicCRUDEndpoints = ({ name }) => {
    const endpoints = {};
    const resourceURL = `v2/${name}`;

    endpoints.getAll = (config = { withCredentials: true, crossDomain: true }, pagination = null) => {
      let url = `${resourceURL}/`;

      if (pagination && (pagination.page || pagination.page_size || pagination.confirmed !== undefined || pagination.device_id)) {
        const params = new URLSearchParams();
        if (pagination.page) {
          params.append('page', pagination.page);
        }
        if (pagination.page_size) {
          params.append('page_size', pagination.page_size);
        }
        if (pagination.confirmed !== undefined && pagination.confirmed !== null && pagination.confirmed !== '') {
          params.append('confirmed', pagination.confirmed);
        }
        if (pagination.device_id) {
          params.append('device_id', pagination.device_id);
        }
        const queryString = params.toString();
        if (queryString) {
          url = `${url}?${queryString}`;
        }
      }

      return axios.get(url, config).catch(err => this.catchFuntion(err));
    };

    endpoints.getOne = ({ id }, config = { withCredentials: true, crossDomain: true }) =>
      axios.get(`${resourceURL}/${id}/`, config).catch(err => this.catchFuntion(err));

    endpoints.create = (toCreate, config = { withCredentials: true, crossDomain: true }) =>
      axios.post(`${resourceURL}/`, toCreate, config).catch(err => this.catchFuntion(err));

    endpoints.update = (toUpdate, config = { withCredentials: true, crossDomain: true }) => {
      const url = (toUpdate.id === '' || toUpdate.id === undefined || toUpdate.id === null)
        ? `${resourceURL}/`
        : `${resourceURL}/${toUpdate.id}/`;
      return axios.put(url, toUpdate, config).catch(err => this.catchFuntion(err));
    };

    endpoints.patch = ({ id }, toPatch, config = { withCredentials: true, crossDomain: true }) =>
      axios.patch(`${resourceURL}/${id}/`, toPatch, config).catch(err => this.catchFuntion(err));

    endpoints.delete = ({ id }, config = { withCredentials: true, crossDomain: true }) =>
      axios.delete(`${resourceURL}/${id}/`, config).catch(err => this.catchFuntion(err));

    if (name === 'device-logs') {
      endpoints.confirm = ({ id }, config = { withCredentials: true, crossDomain: true }) =>
        axios.patch(`${resourceURL}/${id}/confirm`, {}, config).catch(err => this.catchFuntion(err));
    }

      if (name === 'events') {
      endpoints.confirm = ({ id }, config = { withCredentials: true, crossDomain: true }) =>
        axios.patch(`${resourceURL}/${id}/confirm/`, {}, config).catch(err => this.catchFuntion(err));
      endpoints.reset = ({ id }, config = { withCredentials: true, crossDomain: true }) =>
        axios.patch(`${resourceURL}/${id}/reset/`, {}, config).catch(err => this.catchFuntion(err));
      endpoints.probe = ({ id } = {}, config = { withCredentials: true, crossDomain: true }) => {
        const url = id ? `${resourceURL}/${id}/probe/` : `${resourceURL}/probe/`;
        return axios.patch(url, {}, config).catch(err => this.catchFuntion(err));
      };
    }

    if (name === 'devices-request-list') {
      endpoints.getOneWithDateRange = ({ id, dateFrom, dateTo }, config = { withCredentials: true, crossDomain: true }) => {
        const params = new URLSearchParams();
        if (dateFrom) {
          params.append('date_from', dateFrom);
        }
        if (dateTo) {
          params.append('date_to', dateTo);
        }
        const queryString = params.toString();
        const url = queryString ? `${resourceURL}/${id}/?${queryString}` : `${resourceURL}/${id}/`;
        return axios.get(url, config).catch(err => this.catchFuntion(err));
      };
    }

    return endpoints;
  };

  catchFuntion = (error) => {
    try {
      const errorResponse = error && error.response;

      const isJsonParseError = error && error.message && (
        (typeof error.message === 'string' && (error.message.includes('JSON') || error.message.includes('Unexpected end of JSON input') || error.message.includes('unexpected end of data')))
      );
      if (errorResponse === undefined || isJsonParseError) {
        let errorMessage = isJsonParseError
          ? 'Der Server hat keine gültige Antwort gesendet. Bitte später erneut versuchen.'
          : 'Keine Verbindung zum Server möglich.';

        if (error && (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout')))) {
          errorMessage = 'Die Anfrage hat zu lange gedauert. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
        } else if (error && (error.code === 'ERR_NETWORK' || (error.message && error.message.includes('Network Error')))) {
          errorMessage = 'Netzwerkfehler: Keine Verbindung zum Server möglich. Bitte überprüfen Sie Ihre Internetverbindung.';
        } else if (error && error.message) {
          errorMessage = `Verbindungsfehler: ${error.message}`;
        }

        if (window.showErrorNotification) {
          window.showErrorNotification(errorMessage, 'error');
        } else {
          console.error(errorMessage);
        }

        return Promise.reject({
          message: errorMessage,
          isNetworkError: true,
          originalError: error,
          data: null
        });
      }
      else if (errorResponse && errorResponse.status === 401) {
        window.location.replace("/");
        return Promise.resolve({
          data: null,
          error: {
            message: 'Nicht autorisiert',
            status: 401,
            originalError: error
          }
        });
      }
      else {
        let errorMessage = 'Ein Fehler ist aufgetreten.';

        if (errorResponse && errorResponse.data) {
          if (errorResponse.data.message) {
            errorMessage = errorResponse.data.message;
          } else if (errorResponse.data.error) {
            errorMessage = errorResponse.data.error;
          } else if (typeof errorResponse.data === 'string') {
            errorMessage = errorResponse.data;
          }
        }

        const status = errorResponse ? errorResponse.status : 0;
        if (status === 403) {
          errorMessage = 'Zugriff verweigert. Bitte kontaktieren Sie den Administrator.';
        } else if (status === 404) {
          errorMessage = 'Die angeforderte Ressource wurde nicht gefunden.';
        } else if (status === 429) {
          errorMessage = 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.';
        } else if (status >= 500) {
          errorMessage = 'Server-Fehler. Bitte versuchen Sie es später erneut.';
        }

        if (status >= 500 || status === 0 || status === 404) {
          if (window.showErrorNotification) {
            window.showErrorNotification(errorMessage, 'error');
          } else {
            console.error(errorMessage);
          }
        }

        return Promise.reject({
          message: errorMessage,
          status: status,
          data: errorResponse ? errorResponse.data : null,
          originalError: error
        });
      }
    } catch (catchError) {
      console.error('Fehler in catchFuntion:', catchError);
      return Promise.reject({
        message: 'Ein unerwarteter Fehler ist aufgetreten.',
        originalError: catchError,
        data: null
      });
    }
  };
}

export default API;
